import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Quote, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Minus,
} from "lucide-react";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd);
  return data.url;
}

function Btn({ onClick, active, disabled, title, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${
        active ? "bg-bronze text-ivory" : "text-ink/70 hover:bg-blush/20"
      }`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange }) {
  const fileRef = useRef(null);
  const lastHtml = useRef(value || "");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, HTMLAttributes: { class: "rte-image" } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastHtml.current = html;
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: "prose-editor min-h-[220px] max-h-[420px] overflow-y-auto rounded-xl bg-white border border-champagne/40 px-4 py-3 focus:outline-none focus:border-bronze font-body text-sm text-ink",
        "data-testid": "news-form-body-editor",
      },
    },
  });

  // Sync external value changes (e.g. opening an article to edit) without
  // clobbering the live document while the user is typing.
  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next === lastHtml.current) return;
    lastHtml.current = next;
    editor.commands.setContent(next, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const pickImage = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const t = toast.loading("Uploading image…");
    try {
      const url = await uploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
      toast.success("Image inserted", { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed", { id: t });
    }
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 rounded-xl border border-champagne/40 bg-ivory/60 p-1.5 mb-2" data-testid="rte-toolbar">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid="rte-bold"><Bold size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid="rte-italic"><Italic size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-champagne/50" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading" testid="rte-h2"><Heading2 size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading" testid="rte-h3"><Heading3 size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-champagne/50" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testid="rte-ul"><List size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid="rte-ol"><ListOrdered size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testid="rte-quote"><Quote size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider" testid="rte-hr"><Minus size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-champagne/50" />
        <Btn onClick={setLink} active={editor.isActive("link")} title="Add link" testid="rte-link"><LinkIcon size={16} /></Btn>
        <Btn onClick={pickImage} title="Insert image" testid="rte-image"><ImageIcon size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-champagne/50" />
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid="rte-undo"><Undo size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid="rte-redo"><Redo size={16} /></Btn>
      </div>
      <EditorContent editor={editor} />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFile} className="hidden" data-testid="rte-image-input" />
    </div>
  );
}
